import { StrictMode, Suspense, lazy, type ReactNode } from 'react';
import { Loading } from './components/Loading';
import { createRoot } from 'react-dom/client';
import { QueryClient, QueryClientProvider, usePrefetchQuery } from '@tanstack/react-query';
import {
  createBrowserRouter,
  RouterProvider,
  type RouteObject,
  useSearchParams,
  useParams,
} from 'react-router-dom';
import { AuthProvider } from './lib/AuthContext';
import { ThemeProvider } from './lib/ThemeContext';
import { RequireAuth } from './components/RequireAuth';
import { Shell } from './components/Shell';
import { AdminShell } from './components/AdminShell';
import { PopoverProvider } from './components/EntityPopoverContext';
import AuthCallback from './pages/AuthCallback';
import AuthLogout from './pages/AuthLogout';
import { api } from './lib/api';
import { useWorkspace } from './lib/WorkspaceContext';
import {
  LegacyWorkspaceRedirect,
  WorkspaceHomeRedirect,
  WorkspacePathRedirect,
  WorkspaceRoute,
} from './lib/workspaceRoutes';
import '@fontsource-variable/dm-sans';
import '@fontsource-variable/jetbrains-mono';
import './styles/globals.css';
// The prototype's stylesheet, verbatim (P8). Imported after globals so its
// rules win where they overlap — the prototype is the visual source of truth.
import './styles/prototype.css';
import './styles/ask.css';
import { useMe } from './lib/useMe';

const AdminDirectory = lazy(() => import('./pages/AdminDirectory').then(module => ({ default: module.AdminDirectory })));
const AdminAddFiles = lazy(() => import('./pages/AdminDirectory').then(module => ({ default: module.AdminAddFiles })));
const AdminServiceTools = lazy(() => import('./pages/AdminDirectory').then(module => ({ default: module.AdminServiceTools })));
const AdminShadowUploads = lazy(() => import('./pages/AdminShadowUploads').then(module => ({ default: module.AdminShadowUploads })));
const OrgChart = lazy(() => import('./pages/OrgChart'));
const AdminIngestions = lazy(() => import('./pages/AdminIngestions').then(module => ({ default: module.AdminIngestions })));
const AdminBatchDetail = lazy(() => import('./pages/AdminBatchDetail'));
const AdminDupes = lazy(() => import('./pages/AdminDupes').then(module => ({ default: module.AdminDupes })));
const AdminIntegrity = lazy(() => import('./pages/AdminIntegrity').then(module => ({ default: module.AdminIntegrity })));
const AdminEngagement = lazy(() => import('./pages/AdminEngagement').then(module => ({ default: module.AdminEngagement })));
const AdminHeatmapImport = lazy(() => import('./pages/AdminHeatmapImport'));
const AdminExternalData = lazy(() => import('./pages/AdminExternalData').then(module => ({ default: module.AdminExternalData })));
const AdminSuggestions = lazy(() => import('./pages/AdminSuggestions').then(module => ({ default: module.AdminSuggestions })));
const AdminUploadNotifications = lazy(() => import('./pages/AdminUploadNotifications'));
const AdminReviewLater = lazy(() => import('./pages/AdminReviewLater').then(module => ({ default: module.AdminReviewLater })));
const AskStarmap = lazy(() => import('./pages/AskStarmap'));
const Pathfinder = lazy(() => import('./pages/Pathfinder'));
const CoverageExport = lazy(() => import('./pages/CoverageExport'));
const ManagerDash = lazy(() => import('./pages/ManagerDash'));
const WhosInTown = lazy(() => import('./pages/WhosInTown'));
const Snapshots = lazy(() => import('./pages/Snapshots'));
const AccountDetail = lazy(() => import('./pages/AccountDetail'));
const PersonDetail = lazy(() => import('./pages/PersonDetail'));
const IngestionDetail = lazy(async () => {
  const [page] = await Promise.all([
    import('./pages/AdminIngestionDetail'),
    import('../../backend/src/lib/review-readiness'),
  ]);
  return page;
});

function AdminIngestionDetail() {
  const { id = '' } = useParams();
  const { workspaceId } = useWorkspace();
  // Start inside the admin boundary without waiting for the lazy screen's fallback to commit.
  usePrefetchQuery({
    queryKey: ['ingestion', workspaceId, id],
    queryFn: () => api.getIngestion(id, workspaceId),
  });
  return <Suspense fallback={<Loading />}><IngestionDetail /></Suspense>;
}

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 30_000,
      refetchOnWindowFocus: false,
      retry: (n, err) => n < 2 && (err as { status?: number }).status !== 401,
    },
  },
});

const routes: RouteObject[] = [
  { path: '/auth/callback', element: <AuthCallback /> },
  { path: '/auth/logout', element: <AuthLogout /> },
  {
    element: (
      <RequireAuth>
        <Shell />
      </RequireAuth>
    ),
    children: [
      { path: '/', element: <LegacyWorkspaceRedirect to="/" /> },
      {
        path: '/app/:workspaceId',
        element: <WorkspaceRoute />,
        children: [
          { index: true, element: <OrgChart /> },
          { path: 'ask', element: <AskStarmap /> },
          { path: 'paths', element: <Pathfinder /> },
          { path: 'coverage', element: <CoverageExport /> },
          { path: 'manager', element: <ManagerDash /> },
          { path: 'town', element: <WhosInTown /> },
          { path: 'snapshots', element: <Snapshots /> },
          { path: 'account/:id', element: <AccountDetail /> },
          { path: 'person/:pid', element: <PersonDetail /> },
          {
            path: 'admin/quality/heatmap-import',
            element: <RequireAdmin><AdminShell /></RequireAdmin>,
            children: [{ index: true, element: <AdminHeatmapImport /> }],
          },
          {
            path: 'admin',
            element: <RequireAdmin><AdminShell /></RequireAdmin>,
            children: [
              { index: true, element: <AdminIngestions view="reviews" /> },
              { path: 'history', element: <AdminIngestions history /> },
              { path: 'add-files', element: <AdminAddFiles /> },
              { path: 'other', element: <AdminDirectory /> },
              { path: 'service-tools', element: <AdminServiceTools /> },
              { path: 'overview', element: <WorkspacePathRedirect to="/admin" /> },
              { path: 'intake/reviews', element: <AdminIngestions view="reviews" /> },
              { path: 'intake/alerts', element: <AdminUploadNotifications /> },
              { path: 'review-later', element: <AdminReviewLater /> },
              { path: 'intake/shadow', element: <AdminShadowUploads /> },
              { path: 'quality/findings', element: <AdminIntegrity view="findings" /> },
              { path: 'quality/identity', element: <AdminIdentityRoute /> },
              { path: 'quality/engagement', element: <AdminEngagement view="coverage" /> },
              { path: 'sources/imports', element: <AdminExternalData view="imports" /> },
              { path: 'sources/activity', element: <AdminExternalData view="activity" /> },
              { path: 'sources/settings', element: <AdminExternalData view="settings" /> },
              { path: 'feedback/ideas', element: <AdminSuggestions view="product" /> },
              { path: 'feedback/field-intel', element: <AdminSuggestions view="field-intel" /> },
              { path: 'ingestions/:id', element: <AdminIngestionDetail /> },
              { path: 'batches/:id', element: <AdminBatchDetail /> },
              { path: 'engagement', element: <WorkspacePathRedirect to="/admin/quality/engagement" /> },
              { path: 'ingestions', element: <WorkspacePathRedirect to="/admin/intake/reviews" /> },
              { path: 'upload-notifications', element: <WorkspacePathRedirect to="/admin/intake/alerts" /> },
              {
                path: 'dupes',
                element: (
                  <WorkspacePathRedirect
                    to="/admin/quality/identity"
                    searchParams={{ view: 'account-candidates' }}
                  />
                ),
              },
              { path: 'integrity', element: <WorkspacePathRedirect to="/admin/quality/findings" /> },
              { path: 'external-data', element: <WorkspacePathRedirect to="/admin/sources/imports" /> },
              { path: 'suggestions', element: <WorkspacePathRedirect to="/admin/feedback/ideas" /> },
            ],
          },
          { path: '*', element: <WorkspaceHomeRedirect /> },
        ],
      },
      { path: '/ask', element: <LegacyWorkspaceRedirect to="/ask" /> },
      { path: '/paths', element: <LegacyWorkspaceRedirect to="/paths" /> },
      { path: '/coverage', element: <LegacyWorkspaceRedirect to="/coverage" /> },
      { path: '/manager', element: <LegacyWorkspaceRedirect to="/manager" /> },
      { path: '/town', element: <LegacyWorkspaceRedirect to="/town" /> },
      { path: '/snapshots', element: <LegacyWorkspaceRedirect to="/snapshots" /> },
      {
        path: '/account/:id',
        element: (
          <LegacyWorkspaceRedirect
            to={(params) => `/account/${encodeURIComponent(params.id ?? '')}`}
          />
        ),
      },
      {
        path: '/person/:pid',
        element: (
          <LegacyWorkspaceRedirect
            to={(params) => `/person/${encodeURIComponent(params.pid ?? '')}`}
          />
        ),
      },
      { path: '/admin', element: <LegacyWorkspaceRedirect to="/admin" /> },
      { path: '/admin/overview', element: <LegacyWorkspaceRedirect to="/admin" /> },
      { path: '/admin/intake/reviews', element: <LegacyWorkspaceRedirect to="/admin/intake/reviews" /> },
      { path: '/admin/intake/alerts', element: <LegacyWorkspaceRedirect to="/admin/intake/alerts" /> },
      { path: '/admin/quality/findings', element: <LegacyWorkspaceRedirect to="/admin/quality/findings" /> },
      { path: '/admin/quality/identity', element: <LegacyWorkspaceRedirect to="/admin/quality/identity" /> },
      { path: '/admin/quality/engagement', element: <LegacyWorkspaceRedirect to="/admin/quality/engagement" /> },
      { path: '/admin/sources/imports', element: <LegacyWorkspaceRedirect to="/admin/sources/imports" /> },
      { path: '/admin/sources/activity', element: <LegacyWorkspaceRedirect to="/admin/sources/activity" /> },
      { path: '/admin/sources/settings', element: <LegacyWorkspaceRedirect to="/admin/sources/settings" /> },
      { path: '/admin/feedback/ideas', element: <LegacyWorkspaceRedirect to="/admin/feedback/ideas" /> },
      { path: '/admin/feedback/field-intel', element: <LegacyWorkspaceRedirect to="/admin/feedback/field-intel" /> },
      { path: '/admin/engagement', element: <LegacyWorkspaceRedirect to="/admin/quality/engagement" /> },
      { path: '/admin/ingestions', element: <LegacyWorkspaceRedirect to="/admin/intake/reviews" /> },
      { path: '/admin/upload-notifications', element: <LegacyWorkspaceRedirect to="/admin/intake/alerts" /> },
      {
        path: '/admin/ingestions/:id',
        element: (
          <LegacyWorkspaceRedirect
            to={(params) => `/admin/ingestions/${encodeURIComponent(params.id ?? '')}`}
          />
        ),
      },
      {
        path: '/admin/batches/:id',
        element: (
          <LegacyWorkspaceRedirect
            to={(params) => `/admin/batches/${encodeURIComponent(params.id ?? '')}`}
          />
        ),
      },
      {
        path: '/admin/dupes',
        element: (
          <LegacyWorkspaceRedirect
            to="/admin/quality/identity"
            searchParams={{ view: 'account-candidates' }}
          />
        ),
      },
      { path: '/admin/integrity', element: <LegacyWorkspaceRedirect to="/admin/quality/findings" /> },
      { path: '/admin/external-data', element: <LegacyWorkspaceRedirect to="/admin/sources/imports" /> },
      { path: '/admin/suggestions', element: <LegacyWorkspaceRedirect to="/admin/feedback/ideas" /> },
      { path: '*', element: <LegacyWorkspaceRedirect to="/" /> },
    ],
  },
];

function AdminIdentityRoute() {
  const [searchParams] = useSearchParams();
  const view = searchParams.get('view');

  if (view === 'imported-matches') return <AdminExternalData view="matches" />;
  if (view === 'people') return <AdminDupes view="people" />;
  return <AdminDupes view="accounts" />;
}

function RequireAdmin({ children }: { children: ReactNode }) {
  const me = useMe();
  if (me.isLoading) return null;
  if (!me.data?.isAdmin) {
    return (
      <div className="admin-surface mx-auto max-w-3xl px-6 py-12 text-sm text-white/60">
        <h1 className="text-xl font-light tracking-tight text-white">Admin access required</h1>
        <p className="mt-3 text-[var(--text-secondary)]">This area is limited to Starmap admins.</p>
      </div>
    );
  }
  return <div className="admin-surface">{children}</div>;
}

const router = createBrowserRouter(routes);

const root = document.getElementById('root');
if (!root) throw new Error('#root not found');

// Set the theme attribute synchronously, before React mounts, to avoid a
// flash of the wrong palette on hard refreshes. Mirrors ThemeContext's
// initial-value logic.
(() => {
  const saved = window.localStorage.getItem('starmap.theme');
  // Light is the default — P8 parity with the (light) prototype design.
  const theme = saved === 'light' || saved === 'dark' ? saved : 'light';
  document.documentElement.setAttribute('data-theme', theme);
})();

createRoot(root).render(
  <StrictMode>
    <QueryClientProvider client={queryClient}>
      <ThemeProvider>
        <AuthProvider>
          <PopoverProvider>
            <Suspense fallback={<Loading />}><RouterProvider router={router} /></Suspense>
          </PopoverProvider>
        </AuthProvider>
      </ThemeProvider>
    </QueryClientProvider>
  </StrictMode>,
);
